import { useState, useEffect, useMemo } from 'react';
import type { ComputedEventSchedule } from '@/features/events/types/types';

type ViewMode = 'list' | 'calendar';

interface UseEventScheduleViewReturn {
    viewMode: ViewMode;
    setViewMode: (mode: ViewMode) => void;
    currentMonth: Date | null;
    setCurrentMonth: (month: Date) => void;
    availableMonths: Date[];
    paginatedSchedules: ComputedEventSchedule[];
}

// Helper function to get the first day of a month
const getFirstDayOfMonth = (date: Date): Date => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
};

// Helper function to check if two dates are in the same month
const isSameMonth = (date1: Date, date2: Date): boolean => {
    return date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth();
};

export const useEventScheduleView = (
    schedules: ComputedEventSchedule[]
): UseEventScheduleViewReturn => {
    const [viewMode, setViewMode] = useState<ViewMode>('list');
    const [currentMonth, setCurrentMonth] = useState<Date | null>(null);

    // Calculate available months from schedules
    const availableMonths = useMemo(() => {
        if (schedules.length === 0) return [];

        const monthSet = new Set<string>();
        schedules.forEach(schedule => {
            const scheduleDate = new Date(schedule.start_time);
            const monthKey = `${scheduleDate.getFullYear()}-${scheduleDate.getMonth()}`;
            monthSet.add(monthKey);
        });

        const months: Date[] = Array.from(monthSet).map(key => {
            const [year, month] = key.split('-').map(Number);
            return new Date(year, month, 1);
        });

        return months.sort((a, b) => a.getTime() - b.getTime());
    }, [schedules]);

    // Initialize currentMonth to first available month (current month or earliest month with events)
    useEffect(() => {
        if (availableMonths.length > 0 && !currentMonth) {
            const now = new Date();
            const currentMonthFirst = getFirstDayOfMonth(now);

            // Check if current month has events
            const hasCurrentMonth = availableMonths.some(month => isSameMonth(month, currentMonthFirst));

            if (hasCurrentMonth) {
                setCurrentMonth(currentMonthFirst);
            } else {
                // Use the first available month
                setCurrentMonth(availableMonths[0]);
            }
        }
    }, [availableMonths, currentMonth]);

    // Reset to first available month when view mode changes
    useEffect(() => {
        if (availableMonths.length > 0 && viewMode === 'list') {
            const now = new Date();
            const currentMonthFirst = getFirstDayOfMonth(now);
            const hasCurrentMonth = availableMonths.some(month => isSameMonth(month, currentMonthFirst));

            if (hasCurrentMonth) {
                setCurrentMonth(currentMonthFirst);
            } else {
                setCurrentMonth(availableMonths[0]);
            }
        }
    }, [viewMode, availableMonths]);

    // Validate currentMonth is still in availableMonths when schedules change
    useEffect(() => {
        if (currentMonth && availableMonths.length > 0) {
            const isCurrentMonthValid = availableMonths.some(month => isSameMonth(month, currentMonth));
            if (!isCurrentMonthValid) {
                // Current month is no longer available, set to first available month
                setCurrentMonth(availableMonths[0]);
            }
        }
    }, [availableMonths, currentMonth]);

    // Calculate pagination for list view - filter by current month
    const paginatedSchedules = useMemo(() => {
        if (viewMode === 'calendar') {
            return schedules; // Calendar view shows all schedules
        }

        if (!currentMonth) {
            return [];
        }

        return schedules.filter(schedule => {
            const scheduleDate = new Date(schedule.start_time);
            return isSameMonth(scheduleDate, currentMonth);
        });
    }, [schedules, currentMonth, viewMode]);

    return {
        viewMode,
        setViewMode,
        currentMonth,
        setCurrentMonth,
        availableMonths,
        paginatedSchedules
    };
};
